import React, { useEffect, useState } from "react";
import {
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid2,
  Backdrop,
} from "@mui/material";
import Layout from "../components/Layout";
import {
  deleteCourse,
  getAllCourses,
  markCoursePublish,
  updateCourseStatus,
} from "../service/service"; // Import the service function
import { Link } from "react-router-dom";
import {
  Archive,
  Delete,
  Edit,
  Publish,
  Unarchive,
  Visibility,
} from "@mui/icons-material";
import { useAuthContext } from "../context/AuthContext";
import CreateCourseMenu from "../components/CreateCourseMenu";
import ConfirmationModal from "../components/ConfirmationModal";

const Dashboard = () => {
  const { authState } = useAuthContext();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [coursesManaged] = useState(authState?.coursesManaged || []);

  const fetchCourses = async () => {
    setLoading(true); // Start loading
    try {
      const data = await getAllCourses(); // Call the service function
      setCourses(data.data); // Set courses in state
    } catch (error) {
      console.error("Failed to fetch courses:", error);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  useEffect(() => {
    fetchCourses(); // Fetch courses when the component mounts
  }, []);

  const handleCoursePublish = async (courseId) => {
    const res = await markCoursePublish({ courseId });
    if (res.message) {
      alert("Course Published Successfully");
      fetchCourses();
    } else {
      alert("Something went wrong!!");
    }
  };

  const handleCourseStatusChange = async (courseId) => {
    const res = await updateCourseStatus({ courseId });
    if (res.message) {
      alert(res.message);
      fetchCourses();
    } else {
      alert("Something went wrong!!");
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (courseId) => {
    setCourseToDelete(courseId);
    setConfirmText("");
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCourseToDelete(null);
    setConfirmText("");
  };

  // Handle text change in confirmation field
  const handleConfirmTextChange = (e) => {
    setConfirmText(e.target.value);
  };

  // Handle delete course action
  const handleDeleteCourse = async () => {
    if (confirmText.toLowerCase() !== "delete") {
      alert('Please type "delete" to confirm');
      return;
    }

    try {
      const res = await deleteCourse({ courseId: courseToDelete });
      closeDeleteDialog();

      if (res.message) {
        alert(res.message);
        fetchCourses();
      } else {
        alert("Something went wrong while deleting the course!");
      }
    } catch (error) {
      console.error("Error deleting course:", error);
      alert("Failed to delete the course. Please try again.");
    }
  };

  return (
    <Layout>
      {loading ? ( // Show loading indicator
        <Backdrop open={loading} sx={{ zIndex: 1 }}>
          <CircularProgress />
        </Backdrop>
      ) : (
        <>
          {(authState.role === "admin" || authState.role === "creator") && (
            <div className="create-container">
              <CreateCourseMenu label="Create New Course" />
            </div>
          )}

          <Grid2 container spacing={2}>
            {courses.map((course) => (
              <Grid2
                item
                size={{ xs: 12, sm: 6, md: 4 }}
                sx={{ display: "flex" }}
                key={course._id}
              >
                <Card sx={{ width: "100%" }}>
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h5">{course.name}</Typography>
                    <Typography variant="body2">
                      {course.description}
                    </Typography>
                    <div
                      className="course-action-button"
                      style={{ marginTop: "auto" }}
                    >
                      {authState.role === "admin" ||
                      coursesManaged.includes(course._id) ? (
                        <Button
                          variant="contained"
                          color="primary"
                          component={Link}
                          to={`/edit/${course._id}`}
                          style={{ marginTop: "16px", marginRight: "8px" }}
                          endIcon={<Edit />}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          component={Link}
                          to={`/course-detail/${course._id}`}
                          style={{ marginTop: "16px", marginRight: "8px" }}
                          endIcon={<Visibility />}
                        >
                          View
                        </Button>
                      )}

                      {authState.role === "admin" && (
                        <>
                          {!course.isDraft && (
                            <ConfirmationModal
                              title={
                                course.isDeleted
                                  ? "Activate course?"
                                  : "Deactivate course?"
                              }
                              description={
                                course.isDeleted
                                  ? "All learners will gain access to this course."
                                  : "All learners will lose access until reactivated."
                              }
                              helperText={
                                course.isDeleted
                                  ? ""
                                  : "Admin users will still be able to access the course."
                              }
                              confirmLabel={
                                course.isDeleted ? "Activate" : "Deactivate"
                              }
                              cancelLabel="Cancel"
                              buttonLabel={
                                course.isDeleted ? "Activate" : "Deactivate"
                              }
                              loadingLabel={
                                course.isDeleted
                                  ? "Activating..."
                                  : "Deactivating..."
                              }
                              renderTrigger={({ openModal, disabled }) => (
                                <Button
                                  variant="outlined"
                                  color="info"
                                  style={{
                                    marginTop: "16px",
                                    marginRight: "8px",
                                  }}
                                  onClick={openModal}
                                  disabled={disabled}
                                  endIcon={
                                    course.isDeleted ? (
                                      <Unarchive />
                                    ) : (
                                      <Archive />
                                    )
                                  }
                                >
                                  {course.isDeleted ? "Activate" : "Deactivate"}
                                </Button>
                              )}
                              onConfirm={() =>
                                handleCourseStatusChange(course._id)
                              }
                            />
                          )}
                          {course.isDraft && (
                            <ConfirmationModal
                              title="Publish course?"
                              description="The course will become visible to admin users only."
                              helperText="Non-admin users (learners) will NOT be able to access the course."
                              confirmLabel="Publish"
                              cancelLabel="Cancel"
                              buttonLabel="Publish"
                              loadingLabel="Publishing..."
                              renderTrigger={({ openModal, disabled }) => (
                                <Button
                                  variant="outlined"
                                  color="info"
                                  style={{
                                    marginTop: "16px",
                                    marginRight: "8px",
                                  }}
                                  onClick={openModal}
                                  disabled={disabled}
                                  endIcon={<Publish />}
                                >
                                  Publish
                                </Button>
                              )}
                              onConfirm={() => handleCoursePublish(course._id)}
                            />
                          )}
                          <Button
                            variant="outlined"
                            color="error"
                            style={{ marginTop: "16px" }}
                            onClick={() => openDeleteDialog(course._id)}
                            endIcon={<Delete />}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Grid2>
            ))}
          </Grid2>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
            <DialogTitle>Delete Course</DialogTitle>
            <DialogContent>
              <Typography variant="body1" style={{ marginBottom: "16px" }}>
                Are you sure you want to delete this course? This action cannot
                be undone.
              </Typography>
              <Typography variant="body2" style={{ marginBottom: "16px" }}>
                To confirm, type "delete" in the field below:
              </Typography>
              <TextField
                autoFocus
                fullWidth
                value={confirmText}
                onChange={handleConfirmTextChange}
                placeholder="Type 'delete' to confirm"
                variant="outlined"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDeleteDialog} color="primary">
                Cancel
              </Button>
              <Button
                onClick={handleDeleteCourse}
                color="error"
                variant="contained"
                disabled={confirmText.toLowerCase() !== "delete"}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Layout>
  );
};

export default Dashboard;
